import { InfiniteSearchSelect } from '@components';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FC, useEffect, useState } from 'react';
import useGetCountriesList from '@hooks/useGetCountriesList';

interface AddOrEditCompanyModalProps {
  openDialog: boolean;
  handleDialogClose: () => void;
  handleDialogSubmit: (details: { name: string; address?: string; companyHQ?: string }) => void;
  action: 'add' | 'edit';
  companyDetails?: {
    clientCode: string;
    name: string;
    address: string;
    companyHQ?: string;
  };
}
const AddOrEditCompanyModal: FC<AddOrEditCompanyModalProps> = ({
  openDialog,
  handleDialogClose,
  handleDialogSubmit,
  action,
  companyDetails,
}) => {
  const theme = useTheme();
  const [newCompanyDetails, setNewCompanyDetails] = useState<{
    name?: string;
    address?: string;
    companyHQ?: string;
  }>();

  const { countries, onSearchChange, getCountryByCode } = useGetCountriesList();
  useEffect(() => {
    if (companyDetails) {
      setNewCompanyDetails({
        name: companyDetails.name,
        address: companyDetails.address,
        companyHQ: companyDetails?.companyHQ,
      });
    }
  }, [companyDetails]);

  const isEdit = action === 'edit';

  const isModified =
    newCompanyDetails?.name &&
    (newCompanyDetails?.name !== companyDetails?.name ||
      newCompanyDetails?.address !== companyDetails?.address ||
      newCompanyDetails?.companyHQ !== companyDetails?.companyHQ);

  return (
    <Dialog
      open={openDialog}
      onClose={handleDialogClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '40%',
          minWidth: '300px',
          padding: 3,
          borderRadius: '28px',
          height: '570px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: '#162936',
          fontSize: '1.5rem',
          padding: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {isEdit ? 'Edit Company' : 'Add Company'}
        <Box sx={{ fontWeight: 400, color: '#51B8B0' }}>{companyDetails?.clientCode}</Box>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: 0,
          marginTop: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.75rem',
        }}
      >
        <TextField
          margin="dense"
          required
          label="Company Name"
          type="text"
          fullWidth
          value={newCompanyDetails?.name}
          onChange={(e) =>
            setNewCompanyDetails((prev) => ({
              ...prev,
              name: e.target.value,
            }))
          }
          sx={{
            marginBottom: 0,
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.palette.primary.main,
            },
            '& .MuiInputBase-root': {
              color: '#162936',
              fontSize: '1rem',
              fontWeight: 550,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        <TextField
          margin="dense"
          label="Company Address"
          type="text"
          fullWidth
          value={newCompanyDetails?.address}
          onChange={(e) =>
            setNewCompanyDetails((prev) => ({
              ...prev,
              address: e.target.value,
            }))
          }
          sx={{
            margin: 0,
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.palette.primary.main,
            },
            '& .MuiInputBase-root': {
              color: '#162936',
              fontSize: '1rem',
              fontWeight: 550,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <InfiniteSearchSelect
          label="Company HQ"
          options={countries}
          hasNextPage={false}
          isFetching={false}
          fetchNextPage={() => {}}
          onSelect={(option) => {
            setNewCompanyDetails((prev) => ({ ...prev, companyHQ: option.value }));
          }}
          isLoading={false}
          isError={false}
          errorText="Error loading countries"
          noOptionsText="No countries found"
          searchEnabled={true}
          onSearchChange={onSearchChange}
          selectedValue={getCountryByCode(newCompanyDetails?.companyHQ || '')}
          selectStyles={{
            border: '1px solid #737F86',
            borderRadius: '0.25rem',
            fontWeight: '600 !important',
          }}
          menuStyles={{
            paddingTop: '8px',
            top: 60,
            borderRadius: '4px',
            border: '1px solid #737F86',
            maxHeight: '170px',
          }}
          menuItemStyles={{
            fontSize: '15px',
            color: '#162936',
            fontWeight: 550,
            lineHeight: '18px',
            letterSpacing: '-0.6px',
          }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
        }}
      >
        <Button
          variant="outlined"
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            padding: '10px 24px',
            border: 'none',
            borderRadius: '100px',
            color: '#51B8B0',
            '&:hover': {
              border: 'none',
            },
            textTransform: 'none',
          }}
          onClick={handleDialogClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isModified}
          sx={{
            background: theme.palette.primary.main,
            borderRadius: '100px',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            padding: '10px 24px',
            color: '#F6F8F9',
            '&:hover': {
              background: theme.palette.primary.main,
            },
            textTransform: 'none',
          }}
          onClick={() =>
            newCompanyDetails?.name &&
            handleDialogSubmit({
              name: newCompanyDetails.name,
              address: newCompanyDetails.address,
              companyHQ: newCompanyDetails.companyHQ,
            })
          }
        >
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrEditCompanyModal;
